import React, {useState, useEffect} from "react";
import VinImportService from "../../services/VinImportService";
import SimpleDatePicker from "../../components/CFAdminDatePicker";
import './CFAdminUnknownVins.scss';
import CFAdminNotification from "../../components/CFAdminNotification";
import CFAdminDataSelectTable from "../../components/CFAdminDataTable/CFAdminDataSelectTable";
import CFAdminConfirmDialog from "../../components/CFAdminConfirmDialog";

interface VinData {
    id: string;
    vinX: string;
    errorDescription: string;
    modifiedTimestamp: string;
    sentTimestamp: string;
    resolvedTimestamp: string;
}

function CFAdminUnknownVins() {
    const childCompRef = React.useRef<any>();
    const [data, setData] = useState<VinData[]>([]);
    const [startDate, setStartDate] = useState<Date>(new Date());
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingVinPayload, setPendingVinPayload] = useState<string[]>([]);

    const fetchUnknownVins = React.useCallback(async () => {
        try {
            if (!startDate) return;

            const response = await VinImportService.getUnknownVinFragments(startDate);
            setData(response.data);
        } catch (error) {
            console.error("Error fetching unknown VINs:", error);
        }
    }, [startDate]);

    useEffect(() => {
        fetchUnknownVins();
    }, [fetchUnknownVins]);

    useEffect(() => {
        setSelectedRows([]);
    }, [data]);

    const columns = React.useMemo(() => ([
        {
            Header: "VINs",
            accessor: 'vinX'
        },
        {
            Header: "Error Description",
            accessor: 'errorDescription'
        },
        {
            Header: "Modified Timestamp",
            accessor: 'modifiedTimestamp'
        },
        {
            Header: "Sent Timestamp",
            accessor: 'sentTimestamp'
        },
        {
            Header: "Resolved Timestamp",
            accessor: 'resolvedTimestamp'
        }
    ]), []);

    const handleRowSelection = React.useCallback((rows: any[]) => {
        setSelectedRows(rows);
    }, []);

    const selectedVinRows = React.useMemo(
        () => selectedRows.map((row) => row.original as VinData),
        [selectedRows]
    );

    const exportToCsv = (rows: VinData[], fileName: string) => {
        if (!rows.length) {
            return;
        }

        const csvRows = [
            ["VIN", "Error Description", "Modified Timestamp", "Sent Timestamp", "Resolved Timestamp"],
            ...rows.map(row => [row.vinX, row.errorDescription, row.modifiedTimestamp, row.sentTimestamp, row.resolvedTimestamp])
        ];

        const csvContent = csvRows.map(e => e.join(",")).join("\n");
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadAllCSV = () => exportToCsv(data, "unknown_vins.csv");
    const downloadSelectedCSV = () => exportToCsv(selectedVinRows, "unknown_vins_selected.csv");

    const handleSubmitVins = React.useCallback(() => {
        const vinPayload = selectedVinRows.map(({vinX}) => vinX);

        if (!vinPayload.length) {
            childCompRef.current?.failureMessage(
                "Select at least one VIN before submitting."
            );
            return;
        }

        setPendingVinPayload(vinPayload);
        setIsConfirmOpen(true);
    }, [selectedVinRows]);

    const closeConfirmDialog = React.useCallback(() => {
        setIsConfirmOpen(false);
        setPendingVinPayload([]);
    }, []);

    const confirmSubmitVins = React.useCallback(async () => {
        if (!pendingVinPayload.length) {
            closeConfirmDialog();
            return;
        }

        try {
            await VinImportService.handleSubmitVins(pendingVinPayload);
            childCompRef.current?.successMessage(
                `${pendingVinPayload.length} VIN${pendingVinPayload.length > 1 ? 's' : ''} submitted successfully.`
            );
            await fetchUnknownVins();
        } catch (error) {
            console.error("Error submitting VINs:", error);
            const backendMessage = (error as any)?.response?.data?.errorMessage;
            const defaultMessage = "Failed to submit VINs. Please try again.";
            childCompRef.current?.failureMessage(backendMessage || defaultMessage);
        } finally {
            closeConfirmDialog();
        }
    }, [closeConfirmDialog, fetchUnknownVins, pendingVinPayload]);

    return (

        <div className="cf-admin-unknown-vins">
            <h2 className="fomo-type--title">Unknown VINs</h2>
            <div className="unknown-vins-toolbar">
                <div className="unknowns-since-date">
                    <h3 className="unknowns-since-label">Unknown VINs since:</h3>
                    <SimpleDatePicker selectedDate={startDate.toISOString().split('T')[0]}
                                      setSelectedDate={(date: string) => setStartDate(new Date(date))}/>
                </div>
                <p className="unknown-vins-count">Selected VINs: {selectedRows.length}</p>
                <button
                    className="fvp-button button-right-align"
                    onClick={handleSubmitVins}
                    disabled={selectedVinRows.length === 0}
                >
                    Submit VINs
                </button>
            </div>
            <CFAdminDataSelectTable
                columns={columns}
                data={data}
                showAdd={false}
                showFilter={false}
                showSelect={true}
                onRowSelect={handleRowSelection}
            />
            {data.length > 0 && (
                <div className="unknown-vins-actions">
                    <button className="fvp-button" onClick={downloadAllCSV}>
                        Download CSV
                    </button>
                    <button
                        className="fvp-button button-right-align"
                        disabled={selectedVinRows.length === 0}
                        onClick={downloadSelectedCSV}
                    >
                        Download Selected CSV
                    </button>
                </div>
            )}
            <CFAdminConfirmDialog
                isOpen={isConfirmOpen}
                onClose={closeConfirmDialog}
                onConfirm={confirmSubmitVins}
                title="Submit VINs"
                text="Are you sure you want to submit the selected VINs?"
            />
            <CFAdminNotification ref={childCompRef}/>
        </div>
    );
}

export default CFAdminUnknownVins;